import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import DateFilterCalendar from './DateFilterCalendar';
import '../styles/MistakesTab.css';

const API_BASE_URL = window.API_BASE_URL;

function toLocalDayStr(val) {
  if (!val) return null;
  if (typeof val === 'string') {
    const iso = val.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
  }
  const date = new Date(val);
  if (isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function formatFolderDate(dateStr) {
  if (!dateStr) return 'No date';
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function folderKey(row) {
  const cls = String(row.class || '').trim().toLowerCase();
  const subj = String(row.subject || '').trim().toLowerCase();
  const date = toLocalDayStr(row.test_date) || '';
  const teacher = String(row.teacher_name || '').trim().toLowerCase();
  return `${cls}||${subj}||${date}||${teacher}`;
}

function folderTitle(row) {
  const cls = String(row.class || '').trim() || 'Unknown';
  const subj = String(row.subject || '').trim() || 'Unknown';
  return `${cls}-${subj}`;
}

export default function BlankPagesTab({ currentUser }) {
  const [allRecords, setAllRecords] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [filterMode, setFilterMode] = useState('none');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStudent, setFilterStudent] = useState('');
  const [filterTeachers, setFilterTeachers] = useState([]);
  const [filterClasses, setFilterClasses] = useState([]);
  const [filterSubjects, setFilterSubjects] = useState([]);
  const [teacherFilterInput, setTeacherFilterInput] = useState('');
  const [classFilterInput, setClassFilterInput] = useState('');
  const [subjectFilterInput, setSubjectFilterInput] = useState('');
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const [selectedFolderKey, setSelectedFolderKey] = useState(null);
  const isTeacher = currentUser?.role === 'teacher';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/blank-page-records`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => []);
        if (!response.ok) throw new Error((data && data.message) || 'Failed to load blank pages');
        if (!cancelled) setAllRecords(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load blank pages');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const uniqueTeachers = useMemo(
    () => [...new Set(allRecords.map((row) => row.teacher_name).filter(Boolean))].sort(),
    [allRecords]
  );
  const uniqueClasses = useMemo(
    () => [...new Set(allRecords.map((row) => row.class).filter(Boolean))].sort(),
    [allRecords]
  );
  const uniqueSubjects = useMemo(
    () => [...new Set(allRecords.map((row) => row.subject).filter(Boolean))].sort(),
    [allRecords]
  );

  useEffect(() => {
    if (filterMode === 'none' && !filterStartDate && !filterEndDate) {
      setRecords([]);
      return;
    }
    let filtered = allRecords;
    if (filterMode === 'date' && selectedDate) {
      filtered = filtered.filter((row) => toLocalDayStr(row.test_date) === selectedDate);
    }
    if (filterStartDate) {
      filtered = filtered.filter((row) => {
        const day = toLocalDayStr(row.test_date);
        return day && day >= filterStartDate;
      });
    }
    if (filterEndDate) {
      filtered = filtered.filter((row) => {
        const day = toLocalDayStr(row.test_date);
        return day && day <= filterEndDate;
      });
    }
    const student = filterStudent.trim().toLowerCase();
    if (student) {
      filtered = filtered.filter((row) => String(row.student_name || '').toLowerCase().includes(student));
    }
    if (filterTeachers.length) {
      const names = new Set(filterTeachers.map((item) => item.value));
      filtered = filtered.filter((row) => names.has(row.teacher_name));
    }
    if (filterClasses.length) {
      const names = new Set(filterClasses.map((item) => item.value));
      filtered = filtered.filter((row) => names.has(row.class));
    }
    if (filterSubjects.length) {
      const names = new Set(filterSubjects.map((item) => item.value));
      filtered = filtered.filter((row) => names.has(row.subject));
    }
    setRecords(filtered);
  }, [
    allRecords, filterMode, selectedDate, filterStartDate, filterEndDate,
    filterStudent, filterTeachers, filterClasses, filterSubjects
  ]);

  const folderGroups = useMemo(() => {
    const map = new Map();
    for (const row of records) {
      const key = folderKey(row);
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          title: folderTitle(row),
          teacher_name: row.teacher_name || '',
          test_date: toLocalDayStr(row.test_date),
          count: 0
        };
        map.set(key, group);
      }
      group.count += 1;
    }
    return [...map.values()].sort((a, b) => {
      const da = a.test_date || '';
      const db = b.test_date || '';
      if (da !== db) return db.localeCompare(da);
      return a.title.localeCompare(b.title);
    });
  }, [records]);

  const selectedFolder = folderGroups.find((group) => group.key === selectedFolderKey) || null;
  const visibleRecords = selectedFolderKey
    ? records.filter((row) => folderKey(row) === selectedFolderKey)
    : records;

  useEffect(() => {
    if (!selectedFolderKey) return;
    if (!folderGroups.some((group) => group.key === selectedFolderKey)) setSelectedFolderKey(null);
  }, [folderGroups, selectedFolderKey]);

  const compactSelectStyles = (active) => ({
    control: (base) => ({ ...base, borderRadius: 8, fontSize: '0.85rem', minHeight: 38, borderColor: active ? '#3b82f6' : '#ddd' }),
    menu: (base) => ({ ...base, zIndex: 10000 }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menuList: (base) => ({ ...base, maxHeight: 'calc(100vh - 140px)' }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused ? '#f0f4ff' : 'white',
      color: '#111827',
      cursor: 'pointer',
      padding: '4px 12px'
    }),
    placeholder: (base) => ({ ...base, color: active ? '#3b82f6' : base.color })
  });
  const filterSelectMenuProps = {
    maxMenuHeight: typeof window !== 'undefined' ? window.innerHeight - 140 : 800,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuShouldScrollIntoView: false
  };
  const multi = (value, onChange, input, setInput, options, placeholder, active) => (
    <Select
      value={value}
      onChange={(selected) => onChange(selected || [])}
      inputValue={input}
      onInputChange={(val, action) => { if (action.action === 'input-change') setInput(val); }}
      options={options}
      isMulti
      isSearchable
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      {...filterSelectMenuProps}
      placeholder={value.length > 0 ? `${value.length} selected` : placeholder}
      formatOptionLabel={(option, { context }) => {
        if (context === 'value') return <span>{option.label}</span>;
        const checked = value.some((item) => item.value === option.value);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={checked} readOnly style={{ accentColor: '#3b82f6' }} />
            <span>{option.label}</span>
          </div>
        );
      }}
      styles={compactSelectStyles(active)}
    />
  );
  const filtersOn = filterTeachers.length || filterClasses.length || filterSubjects.length || filterStudent.trim();

  return (
    <div className="mistakes-tab">
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
          }}>
            <i className="fa-solid fa-file-lines" style={{ color: 'white', fontSize: 18 }}></i>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            {selectedFolder ? (
              <>
                <button type="button" onClick={() => setSelectedFolderKey(null)} className="mistakes-back-btn" title="Back to folder list">
                  <i className="fa-solid fa-arrow-left"></i>
                  Folders
                </button>
                <h1 style={{ color: 'var(--text-primary)', margin: '6px 0 0 0', fontSize: '1.125rem', fontWeight: 700 }}>
                  {selectedFolder.title}
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.9rem' }}> ({visibleRecords.length})</span>
                </h1>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {formatFolderDate(selectedFolder.test_date)}
                  {selectedFolder.teacher_name ? ` · ${selectedFolder.teacher_name}` : ''}
                </p>
              </>
            ) : (
              <>
                <h1 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>
                  Blank page folders ({folderGroups.length})
                </h1>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {records.length} blank page{records.length === 1 ? '' : 's'} — click a folder to open it
                </p>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <i className="fa-solid fa-user-graduate" style={{ color: '#0EA5E9' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Student:</label>
              <input
                type="text"
                value={filterStudent}
                onChange={(e) => setFilterStudent(e.target.value)}
                placeholder="Filter by name"
                style={{
                  flex: 1, minWidth: 140, padding: '7px 10px', borderRadius: 8, fontSize: 13,
                  border: `1px solid ${filterStudent.trim() ? '#3b82f6' : 'var(--border, #d1d5db)'}`,
                  background: 'var(--surface, white)', color: 'var(--text-primary)'
                }}
              />
            </div>
            {!isTeacher && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
                <i className="fa-solid fa-user-tie" style={{ color: '#8B5CF6' }}></i>
                <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Teacher:</label>
                <div style={{ flex: 1, minWidth: 140 }}>
                  {multi(filterTeachers, setFilterTeachers, teacherFilterInput, setTeacherFilterInput, uniqueTeachers.map((name) => ({ value: name, label: name })), 'All Teachers', filterTeachers.length > 0)}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 180 }}>
              <i className="fa-solid fa-graduation-cap" style={{ color: '#3B82F6' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Class:</label>
              <div style={{ flex: 1, minWidth: 120 }}>
                {multi(filterClasses, setFilterClasses, classFilterInput, setClassFilterInput, uniqueClasses.map((name) => ({ value: name, label: name })), 'All Classes', filterClasses.length > 0)}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 180 }}>
              <i className="fa-solid fa-book" style={{ color: '#10b981' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>Subject:</label>
              <div style={{ flex: 1, minWidth: 120 }}>
                {multi(filterSubjects, setFilterSubjects, subjectFilterInput, setSubjectFilterInput, uniqueSubjects.map((name) => ({ value: name, label: name })), 'All Subjects', filterSubjects.length > 0)}
              </div>
            </div>
            {filtersOn && (
              <button
                type="button"
                onClick={() => {
                  setFilterStudent('');
                  setFilterTeachers([]);
                  setFilterClasses([]);
                  setFilterSubjects([]);
                }}
                style={{
                  padding: '8px 12px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: 'white', fontWeight: 700, fontSize: 13
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <p style={{ color: '#991B1B', fontWeight: 700 }}>{error}</p>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setCalendarCollapsed((on) => !on)}
          style={{
            border: '1px solid var(--border, #d1d5db)', background: 'var(--surface, white)',
            borderRadius: 8, padding: '6px 10px', fontWeight: 700, fontSize: 12, cursor: 'pointer'
          }}
        >
          <i className={`fa-solid fa-${calendarCollapsed ? 'eye' : 'eye-slash'}`} style={{ marginRight: 6 }}></i>
          {calendarCollapsed ? 'Show calendar' : 'Hide calendar'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {!calendarCollapsed && (
          <div style={{ width: 360, flexShrink: 0 }}>
            <DateFilterCalendar
              items={allRecords}
              dateField="test_date"
              selectedDate={filterMode === 'date' ? selectedDate : ''}
              onDateChange={(dateStr) => {
                if (dateStr) {
                  setSelectedDate(dateStr);
                  setFilterMode('date');
                } else {
                  setSelectedDate('');
                  setFilterMode('none');
                }
              }}
              itemLabelSingular="blk"
              itemLabelPlural="blk"
              itemLabelSeparator="-"
              title="Select Date"
              columns={5}
            />
            <div className="filters-section" style={{ marginTop: 12 }}>
              <h3>Select Date Range</h3>
              <div className="filter-group" style={{ marginBottom: 10 }}>
                <label>From Date:</label>
                <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
              </div>
              <div className="filter-group">
                <label>To Date:</label>
                <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
              </div>
              {(selectedDate || filterStartDate || filterEndDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDate('');
                    setFilterMode('none');
                    setFilterStartDate('');
                    setFilterEndDate('');
                  }}
                  style={{
                    marginTop: 10, border: 'none', borderRadius: 8, padding: '6px 10px',
                    background: '#DC2626', color: 'white', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Clear Date
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {loading && <p style={{ color: 'var(--text-muted)', fontWeight: 700 }}>Loading blank pages…</p>}
          {!loading && (filterMode !== 'none' || filterStartDate || filterEndDate) && records.length === 0 && (
            <div className="no-records"><p>No unexpected blank pages in this filter.</p></div>
          )}
          {!loading && !selectedFolderKey && records.length > 0 && (
            <div className="mistake-folders-grid">
              {folderGroups.map((folder) => (
                <button
                  type="button"
                  key={folder.key}
                  className="mistake-folder-card"
                  onClick={() => setSelectedFolderKey(folder.key)}
                  title={`Open blank pages for ${folder.title}`}
                >
                  <div className="mistake-folder-icon">
                    <i className="fa-solid fa-folder"></i>
                  </div>
                  <div className="mistake-folder-info">
                    <h3 className="mistake-folder-title">{folder.title}</h3>
                    <div className="mistake-folder-meta">
                      <span>{formatFolderDate(folder.test_date)}</span>
                      {folder.teacher_name ? <span>{folder.teacher_name}</span> : null}
                    </div>
                    <div className="mistake-folder-count">
                      {folder.count} {folder.count === 1 ? 'blank page' : 'blank pages'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {!loading && selectedFolderKey && visibleRecords.length > 0 && (
            <div className="records-grid">
              {visibleRecords
                .slice()
                .sort((a, b) => String(a.student_name || '').localeCompare(String(b.student_name || '')))
                .map((row) => (
                  <div key={row.id} className="record-card">
                    <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>{row.student_name}</h3>
                    <p style={{ margin: 0, color: '#991B1B', fontWeight: 800 }}>{row.page_label}</p>
                    {row.image_name ? (
                      <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 12 }}>{row.image_name}</p>
                    ) : null}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
